/**
 * G5. Свадьба: «стол». Выбрать 3 из 6 расходов (гости, еда, платье, музыка,
 * фото, свадьба без гостей). Цена каждого — доля ВВП на душу (ППС) страны,
 * честно в долларах на экране; счастье +3 + радость выбранного (гости +2,
 * еда +2, музыка +1, остальное 0), итого +3…+8. Родители «помогают»
 * половиной суммы с p = дециль/10.
 *
 * Триггер — запись решения `decision.love.partner.seek.found` (пара найдена);
 * в самом решении love.partner варианта брака нет, а исход жребия «нашли /
 * не нашли» известен только после него. Один раз на жизнь (`flags.weddingDone`).
 * «Пропустить» — три самых дешёвых расхода без помощи родителей.
 */
import type { Dataset, GamblingLaw } from '@life/data';
import type { Life, MinigameOutcome, Rng } from '@life/engine';
import type { MinigameDef } from '../types';
import { gdpPpp } from '../casino/common';
import { distinctIndexes, nothing } from './common';

export const WEDDING_TRIGGER_EVENT = 'decision.love.partner.seek.found';
export const FLAG_WEDDING_DONE = 'weddingDone';
export const WEDDING_PICKS = 3;
export const WEDDING_HAPPINESS_BASE = 3;
export const WEDDING_HAPPINESS_MAX = 8;
/** Доля стоимости, которую покрывают родители. */
export const WEDDING_PARENT_HELP_SHARE = 0.5;

export interface WeddingItemDef {
  id: string;
  /** Доля ВВП на душу. */
  share: number;
  /** Вклад в счастье. */
  joy: number;
}

export const WEDDING_ITEMS: readonly WeddingItemDef[] = [
  { id: 'guests', share: 0.3, joy: 2 },
  { id: 'food', share: 0.25, joy: 2 },
  { id: 'dress', share: 0.15, joy: 0 },
  { id: 'music', share: 0.1, joy: 1 },
  { id: 'photo', share: 0.08, joy: 0 },
  { id: 'none', share: 0, joy: 0 },
];

export interface WeddingItem {
  id: string;
  key: string;
  cost: number;
  joy: number;
}

export interface WeddingState {
  items: WeddingItem[];
  picks: number;
  gdp: number;
  money: number;
  /** Вероятность помощи родителей. */
  helpP: number;
}

export type WeddingInput = { picks: number[] };

export function weddingItemKey(id: string): string {
  return `minigame.wedding.item.${id}`;
}

/** p помощи родителей по децилю достатка семьи 1..10. */
export function weddingHelpP(decile: number): number {
  const d = Number.isFinite(decile) ? Math.min(10, Math.max(1, decile)) : 1;
  return d / 10;
}

function result(items: readonly WeddingItem[], helped: boolean): MinigameOutcome {
  const cost = items.reduce((s, it) => s + it.cost, 0);
  const joy = items.reduce((s, it) => s + it.joy, 0);
  const paid = helped ? cost * (1 - WEDDING_PARENT_HELP_SHARE) : cost;
  const help = cost - paid;
  return {
    ...nothing(),
    moneyDelta: -paid,
    stats: { happiness: Math.min(WEDDING_HAPPINESS_MAX, WEDDING_HAPPINESS_BASE + joy) },
    flags: { [FLAG_WEDDING_DONE]: true },
    logKey: helped ? 'minigame.wedding.helped' : 'minigame.wedding.done',
    logParams: helped ? { amount: Math.round(paid), help: Math.round(help) } : { amount: Math.round(paid) },
  };
}

export const wedding: MinigameDef<WeddingInput, WeddingState> = {
  id: 'wedding',
  section: 'G',
  triggers: [{ kind: 'event', event: WEDDING_TRIGGER_EVENT }],
  available(life: Life, _ds: Dataset, _law: GamblingLaw): boolean {
    return life.partner && life.flags[FLAG_WEDDING_DONE] !== true;
  },
  setup(life: Life, ds: Dataset): WeddingState {
    const gdp = Math.max(0, gdpPpp(ds, life));
    const items = WEDDING_ITEMS.map((it) => ({ id: it.id, key: weddingItemKey(it.id), cost: it.share * gdp, joy: it.joy }));
    return { items, picks: WEDDING_PICKS, gdp, money: Math.max(0, life.money), helpP: weddingHelpP(life.familyDecile) };
  },
  play(state: WeddingState, input: WeddingInput, _life: Life, rng: Rng): MinigameOutcome {
    const picks = distinctIndexes(input.picks, state.items.length).slice(0, state.picks);
    const chosen = picks.map((i) => state.items[i] as WeddingItem);
    return result(chosen, rng.chance(state.helpP));
  },
  expected(state: WeddingState): MinigameOutcome {
    const cheapest = [...state.items].sort((a, b) => a.cost - b.cost).slice(0, state.picks);
    return result(cheapest, false);
  },
};
